import React, { useState, useEffect } from "react";
import { Table, Select, Spin } from 'antd';
import { EnvironmentOutlined } from '@ant-design/icons';
import { getJobListPublicApi } from "api/services/jobs";
import { toast } from 'react-toastify';
import EssFullWidthModal from "components/modelpopup/baseModal";

const { Option } = Select;

const JobSelectionModal = ({ visible, onCancel, onJobSelect }) => {
  const [loading, setLoading] = useState(false);
  const [jobList, setJobList] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState("all");
  const [departments, setDepartments] = useState([]);

  useEffect(() => {
    if (visible) {
      fetchJobs();
    }
  }, [visible]);

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const response = await getJobListPublicApi({});
      const jobs = response.data.results || [];
      setJobList(jobs);
      setFilteredJobs(jobs);
      
      // Extract unique departments for filter
      const uniqueDepartments = [...new Set(jobs.map(job => 
        job.department?.name
      ))].filter(Boolean);
      setDepartments(uniqueDepartments);
    } catch (error) {
      console.error("Error fetching jobs:", error);
      toast.error('Failed to load jobs. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Filter jobs based on search term and department
    let filtered = jobList.filter(job => {
      const matchesSearch = job?.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          job?.department?.name?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesDepartment = selectedDepartment === "all" || 
                              job?.department?.name === selectedDepartment;
      return matchesSearch && matchesDepartment;
    });
    setFilteredJobs(filtered);
  }, [jobList, searchTerm, selectedDepartment]);

  const handleJobSelect = (job) => {
    onJobSelect(job);
    onCancel();
  };

  const columns = [
    {
      title: "Job Title",
      dataIndex: "title",
      key: "title",
      render: (text, record) => (
        <div className="job-title">
          <h6 className="mb-1">{text}</h6>
          <small className="text-muted">{record.department?.name}</small>
        </div>
      ),
    },
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
      render: (text) => (
        <span>
          <EnvironmentOutlined className="me-1" />
          {text || "Not specified"}
        </span>
      ),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (text) => (
        <span className="badge bg-primary">{text || "Full Time"}</span>
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <button
          className="btn btn-sm btn-primary"
          onClick={() => handleJobSelect(record)}
        >
          Select Job
        </button>
      ),
    },
  ];

  return (
    <EssFullWidthModal
      title="Select a Job to Refer"
      open={visible}
      onCancel={onCancel}
      mask={false}
      className="full-width-modal"
    >
      <div className="modal-content">
        <div className="modal-body">
          <div className="mb-3">
            <div className="row">
              <div className="col-md-6">
                <div className="input-group">
                  <span className="input-group-text">
                    <i className="fa fa-search"></i>
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search jobs..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
              <div className="col-md-6">
                <Select
                  placeholder="Filter by department"
                  value={selectedDepartment}
                  onChange={setSelectedDepartment}
                  style={{ width: '100%' }}
                >
                  <Option value="all">All Departments</Option>
                  {departments.map(dept => (
                    <Option key={dept} value={dept}>{dept}</Option>
                  ))}
                </Select>
              </div>
            </div>
          </div>

          <Table
            columns={columns}
            dataSource={filteredJobs}
            rowKey="id"
            loading={loading}
            pagination={{
              pageSize: 5,
              showSizeChanger: false,
            }}
            size="small"
          />
        </div>
      </div>
    </EssFullWidthModal>
  );
};

export default JobSelectionModal;
